import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// ── Fix Leaflet default icon paths ───────────────────────────────────────────
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl:       "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl:     "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// ── Paleta de cores por tipo de ponto ────────────────────────────────────────
const POINT_COLORS = {
  PV:      "#2563eb",
  TIL:     "#16a34a",
  TSL:     "#ea580c",
  CX:      "#7c3aed",
  default: "#6b7280",
};

function getPointColor(tipo) {
  if (!tipo) return POINT_COLORS.default;
  const upper = tipo.toUpperCase();
  for (const key of Object.keys(POINT_COLORS)) {
    if (upper.includes(key)) return POINT_COLORS[key];
  }
  return POINT_COLORS.default;
}

// ── Ícone circular customizado ───────────────────────────────────────────────
const makeCircleIcon = (color) =>
  L.divIcon({
    className: "",
    html: `<div style="
      width:13px;height:13px;border-radius:50%;
      background:${color};border:2px solid #fff;
      box-shadow:0 0 5px rgba(0,0,0,.45);
    "></div>`,
    iconSize:    [13, 13],
    iconAnchor:  [6, 6],
    popupAnchor: [0, -10],
  });

// ── Popup: ponto ─────────────────────────────────────────────────────────────
function buildPointPopup(props) {
  const fields = [
    ["Tipo",       props.tipo],
    ["Cota Tampa", props.cota_tampa_m != null ? `${props.cota_tampa_m} m` : null],
    ["Cota Fundo", props.cota_fundo_m  != null ? `${props.cota_fundo_m} m`  : null],
    ["Leste UTM",  props.leste_utm_m   != null ? `${props.leste_utm_m} m`   : null],
    ["Norte UTM",  props.norte_utm_m   != null ? `${props.norte_utm_m} m`   : null],
  ].filter(([, v]) => v != null && v !== "");

  const rows = fields
    .map(([k, v]) => `
      <tr>
        <td style="padding:2px 10px 2px 0;color:#6b7280;font-size:11px;white-space:nowrap">${k}</td>
        <td style="padding:2px 0;font-size:12px;font-weight:600">${v}</td>
      </tr>`)
    .join("");

  return `
    <div class="map-popup">
      <strong class="map-popup-title blue">📍 ${props.id_poco || "Ponto"}</strong>
      ${rows ? `<table style="border-collapse:collapse">${rows}</table>` : ""}
    </div>`;
}

// ── Popup: trecho ────────────────────────────────────────────────────────────
function buildTrechoPopup(props) {
  const fields = [
    ["Montante",    props.poco_montante_id],
    ["Jusante",     props.poco_jusante_id],
    ["Extensão",    props.extensao_m   != null ? `${props.extensao_m} m`   : null],
    ["Diâmetro",    props.diametro_mm  != null ? `${props.diametro_mm} mm` : null],
    ["Material",    props.material],
    ["Mét. Const.", props.metodo_construtivo],
  ].filter(([, v]) => v != null && v !== "");

  const rows = fields
    .map(([k, v]) => `
      <tr>
        <td style="padding:2px 10px 2px 0;color:#6b7280;font-size:11px;white-space:nowrap">${k}</td>
        <td style="padding:2px 0;font-size:12px;font-weight:600">${v}</td>
      </tr>`)
    .join("");

  return `
    <div class="map-popup">
      <strong class="map-popup-title red">🔗 ${props.id_trecho || "Trecho"}</strong>
      ${rows ? `<table style="border-collapse:collapse">${rows}</table>` : ""}
    </div>`;
}

// ── Legenda (React, flutua sobre o mapa) ─────────────────────────────────────
const LEGEND_ITEMS = [
  { color: POINT_COLORS.PV,      label: "Poço de Visita (PV)" },
  { color: POINT_COLORS.TIL,     label: "Terminal Inferior (TIL)" },
  { color: POINT_COLORS.TSL,     label: "Terminal Superior (TSL)" },
  { color: POINT_COLORS.CX,      label: "Caixa (CX)" },
  { color: POINT_COLORS.default, label: "Outros pontos" },
];

function MapLegend({ counts }) {
  return (
    <div style={{
      position: "absolute",
      bottom: 28,
      left: 10,
      zIndex: 400,
      background: "rgba(255,255,255,0.93)",
      backdropFilter: "blur(4px)",
      border: "1px solid #e2e8f0",
      borderRadius: 8,
      padding: "8px 12px",
      fontSize: 11,
      fontFamily: "sans-serif",
      lineHeight: 1.6,
      minWidth: 155,
      boxShadow: "0 2px 6px rgba(0,0,0,.15)",
      pointerEvents: "none",
    }}>
      <div style={{ fontWeight: 700, fontSize: 12, marginBottom: 6, color: "#1e293b" }}>
        Legenda
      </div>

      {LEGEND_ITEMS.map(({ color, label }) => (
        <div key={label} style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
          <div style={{
            width: 10, height: 10, borderRadius: "50%",
            background: color, border: "2px solid #fff",
            boxShadow: "0 0 3px rgba(0,0,0,.35)", flexShrink: 0,
          }} />
          <span style={{ color: "#374151" }}>{label}</span>
        </div>
      ))}

      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 3 }}>
        <div style={{
          width: 18, height: 3, background: "#ef4444",
          borderRadius: 2, flexShrink: 0,
        }} />
        <span style={{ color: "#374151" }}>Trecho de rede</span>
      </div>

      {counts && (
        <div style={{
          marginTop: 7, paddingTop: 6,
          borderTop: "1px solid #e5e7eb",
          color: "#6b7280", fontSize: 10,
        }}>
          <div>Pontos: <strong style={{ color: "#1e293b" }}>{counts.points}</strong></div>
          <div>Trechos: <strong style={{ color: "#1e293b" }}>{counts.lines}</strong></div>
        </div>
      )}
    </div>
  );
}

// ── Componente principal ──────────────────────────────────────────────────────
/**
 * MapVisualizer
 *
 * Props:
 *   data – GeoJSON FeatureCollection retornado pelo backend ANDRE
 *
 * Classes CSS consumidas (definidas em Andre.css):
 *   .map-wrapper  .map-empty  .map-badge
 *   .map-popup  .map-popup-title.blue  .map-popup-title.red
 */
export default function MapVisualizer({ data }) {
  const containerRef = useRef(null);
  const mapRef       = useRef(null);
  const layersRef    = useRef([]);
  const [counts, setCounts] = useState(null);

  // Inicializa o mapa uma única vez
  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = L.map(containerRef.current, {
      center:      [-23.5, -46.6],
      zoom:        13,
      zoomControl: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(map);

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  // Corrige mapa "em branco" quando o container ainda estava oculto na montagem
  useEffect(() => {
    const t = setTimeout(() => mapRef.current?.invalidateSize(), 200);
    return () => clearTimeout(t);
  }, []);

  // Desenha as features quando `data` muda
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    layersRef.current.forEach((l) => map.removeLayer(l));
    layersRef.current = [];

    if (!data?.features?.length) {
      setCounts(null);
      return;
    }

    const bounds     = [];
    let   pointCount = 0;
    let   lineCount  = 0;

    data.features.forEach(({ geometry, properties }) => {
      if (!geometry) return;

      if (geometry.type === "Point") {
        const [lon, lat] = geometry.coordinates;
        const marker = L.marker([lat, lon], {
          icon: makeCircleIcon(getPointColor(properties?.tipo)),
        }).bindPopup(buildPointPopup(properties || {}), { maxWidth: 260 });

        marker.addTo(map);
        layersRef.current.push(marker);
        bounds.push([lat, lon]);
        pointCount++;

      } else if (geometry.type === "LineString") {
        const latlngs = geometry.coordinates.map(([lon, lat]) => [lat, lon]);
        const line = L.polyline(latlngs, { color: "#ef4444", weight: 3, opacity: 0.85 })
          .bindPopup(buildTrechoPopup(properties || {}), { maxWidth: 280 });

        line.addTo(map);
        layersRef.current.push(line);
        latlngs.forEach((ll) => bounds.push(ll));
        lineCount++;
      }
    });

    setCounts({ points: pointCount, lines: lineCount });

    if (bounds.length > 0) {
      try {
        map.fitBounds(L.latLngBounds(bounds), { padding: [40, 40] });
      } catch { /* bounds inválido */ }
    }
  }, [data]);

  const hasData = Boolean(data?.features?.length);

  return (
    // .map-wrapper já define: width 100%, height 100%, position relative, overflow hidden
    <div className="map-wrapper">

      {/* Container do Leaflet — preenche o wrapper inteiro */}
      <div ref={containerRef} style={{ width: "100%", height: "100%" }} />

      {/* Badge WGS84 — posicionado pelo .map-badge do CSS */}
      {hasData && <div className="map-badge">WGS 84</div>}

      {/* Legenda flutuante */}
      {hasData && <MapLegend counts={counts} />}

      {/* Placeholder sem dados — centralizado pelo .map-empty */}
      {!hasData && (
        <div className="map-empty">
          Nenhum dado espacial disponível.&nbsp;
          Envie e analise arquivos para visualizar o mapa.
        </div>
      )}
    </div>
  );
}
